import { reportAnalyticsError } from './analytics';
/**
 * Store Monitor — reporter do customizador Turbine.
 *
 * Quando o customizador roda embutido (iframe na loja), envia os erros reais
 * da navegação do cliente para a janela pai (onde o beacon do Store Monitor
 * escuta `message` com __turbineMonitor) — assim falhas de modelo 3D, textura,
 * WebGL, configuração, etc. viram problemas monitorados na área Personalização/Turbine.
 *
 * Não faz nada se não estiver embutido (uso direto do app não reporta).
 */

// Marcadores críticos de WARN que indicam falha real (a maioria dos warns do
// turbine são fallbacks normais e devem ser ignorados).
const CRITICAL_WARN = [
  'webgl context lost',
  'scene not initialized',
  'no materials found',
  'produced no geometry',
];

function isEmbedded(): boolean {
  try {
    return window.parent && window.parent !== window;
  } catch {
    return true; // cross-origin → assume embutido
  }
}

function post(marker: string, message?: string, detail?: Record<string, unknown>) {
  // Clarity recebe so a CATEGORIA (sem mensagem, URL ou stack); o detalhe segue
  // para o Store Monitor como sempre.
  reportAnalyticsError(marker);
  try {
    const pathMatch = window.location.pathname.match(/\/customize\/(\d+)/);
    const params = new URLSearchParams(window.location.search);
    const wrapper = document.querySelector<HTMLElement>('.customizer-wrapper');
    const step = document.querySelector<HTMLElement>('[data-step], [data-customizer-step], .customizer-menu__step--active');
    const context = {
      productId: pathMatch?.[1] || null,
      variantId: params.get('variant'),
      mediaOnly: params.get('mediaOnly') === 'true',
      embedded: params.get('embed') === 'true',
      customizerStep: step?.getAttribute('data-step') || step?.getAttribute('data-customizer-step') || step?.innerText?.trim().slice(0, 120) || null,
      iframeUrl: `${window.location.pathname}${window.location.search}`.slice(0, 500),
      customizerState: wrapper?.className || null,
    };
    window.parent.postMessage(
      {
        __turbineMonitor: true,
        marker: String(marker).slice(0, 160),
        message: message ? String(message).slice(0, 400) : '',
        detail: { ...context, ...(detail ?? {}) },
      },
      '*',
    );
  } catch {
    /* monitoramento nunca quebra o customizador */
  }
}

/**
 * Reporte explícito de problema do customizador (use nos pontos onde uma falha
 * é silenciosa — ex.: modelo/componente não aplicou). Seguro fora do iframe.
 */
export function reportTurbineIssue(marker: string, message?: string, detail?: Record<string, unknown>) {
  if (!isEmbedded()) return;
  post(marker, message, detail);
}

let installed = false;

export function installStoreMonitorReporter() {
  if (installed || !isEmbedded()) return;
  installed = true;

  // 1) Erros JS, promises rejeitadas E recursos quebrados (imagem/ícone/fonte/CSS).
  //    capture=true é necessário para capturar erros de carregamento de recursos.
  window.addEventListener(
    'error',
    (e) => {
      const t = e && (e.target as HTMLElement | null);
      if (t && t !== (window as unknown as HTMLElement) && t.tagName) {
        const tag = t.tagName.toUpperCase();
        // SOURCE também representa vídeo e alternativas lazy de PICTURE; só IMG
        // comprova que uma imagem/ícone tentou carregar.
        if (tag === 'IMG') {
          const src = (t as HTMLImageElement).currentSrc || (t as HTMLImageElement).src || '';
          if (src) {
            const img = t as HTMLImageElement;
            window.setTimeout(() => {
              if (!document.contains(img)) return;
              if (img.complete && img.naturalWidth > 0) return;
              const retry = new Image();
              retry.onerror = () => post('Imagem/ícone não carregou no personalizador', src, {
                elementTag: 'IMG',
                retryFailed: true,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight,
              });
              retry.src = src;
            }, 1_200);
          }
          return;
        }
        if (tag === 'LINK') {
          const link = t as HTMLLinkElement;
          const href = link.href || '';
          const lower = href.toLowerCase();
          if (/\.(glb|gltf|usdz|fbx|obj)(\?|$)/.test(lower)) {
            // GLBs são antecipados por <link rel="preload" as="fetch">. Safari
            // pode cancelar/ignorar esse hint (troca de variante, pressão de
            // memória ou prioridade) e disparar `error`, mesmo quando o
            // GLTFLoader baixa o mesmo arquivo normalmente logo depois. O
            // carregador real faz retry e reporta a falha persistente com URL,
            // produto e papel do modelo; portanto um erro do LINK sozinho não
            // comprova modelo quebrado e não deve abrir issue.
            return;
          } else if (/\.(woff2?|ttf|otf|eot)(\?|$)/.test(lower)) {
            post('Fonte da personalização não carregou', href);
          } else if (/\.css(\?|$)/.test(lower)) {
            post('CSS não carregou no personalizador', href);
          } else {
            post('Recurso não carregou no personalizador', href);
          }
          return;
        }
        if (tag === 'SCRIPT') {
          post('Script não carregou no personalizador', (t as HTMLScriptElement).src || '');
          return;
        }
      }
      if (e && e.message) post(e.message, e.filename ? `${e.filename}:${e.lineno}` : undefined);
    },
    true,
  );
  window.addEventListener('unhandledrejection', (e) => {
    const r = (e && (e as PromiseRejectionEvent).reason) as { message?: string } | string | undefined;
    const msg = typeof r === 'string' ? r : r?.message;
    if (msg) post('Promise rejeitada', msg);
  });

  // 2) Falha de carregamento de FONTE (estilos de personalização)
  try {
    const fonts = (document as unknown as { fonts?: FontFaceSet & { addEventListener?: (t: string, cb: (ev: unknown) => void) => void } }).fonts;
    if (fonts && typeof fonts.addEventListener === 'function') {
      fonts.addEventListener('loadingerror', (ev: unknown) => {
        const faces = (ev as { fontfaces?: { family?: string }[] }).fontfaces || [];
        const fams = faces.map((f) => f.family).filter(Boolean).join(', ');
        post('Fonte da personalização não carregou', fams || 'fonte desconhecida');
      });
    }
  } catch {
    /* noop */
  }

  // 3) Intercepta console.error (sempre) e console.warn (só marcadores críticos)
  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      const text = args.map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : '')).join(' ').trim();
      if (text) post(text);
    } catch {
      /* noop */
    }
    origError(...args);
  };
  console.warn = (...args: unknown[]) => {
    try {
      const text = args.map((a) => (typeof a === 'string' ? a : '')).join(' ').toLowerCase();
      if (CRITICAL_WARN.some((m) => text.includes(m))) {
        post(args.map((a) => (typeof a === 'string' ? a : '')).join(' ').trim());
      }
    } catch {
      /* noop */
    }
    origWarn(...args);
  };
}
